import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip,
  FormHelperText,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { axiosGetCall } from 'src/services/apisCall';

export default function UserSelectCard({ selectedUser, onUserSelect, error }) {
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [inputValue, setInputValue] = useState('');

  const debounceRef = useRef(null);

  const fetchUserList = useCallback(async (search = '') => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = { limit: 50 };
      if (search?.trim()) params.email = search.trim();

      const { usersList } = await axiosGetCall('consoleUsersList', params);
      setUserList(usersList || []);
    } catch (err) {
      setFetchError(err?.message || 'Failed to load user list. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserList('');
  }, [fetchUserList]);

  const handleInputChange = (_, value) => {
    setInputValue(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchUserList(value);
    }, 400);
  };

  const getLabel = (user) => {
    if (!user) return '';
    const parts = [user.email, user.name, user.userId].filter(Boolean);
    return parts.join(' — ');
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Select User
      </Typography>

      {/* API Fetch Error State */}
      {fetchError && !loading && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Tooltip title="Retry">
              <IconButton size="small" onClick={() => fetchUserList(inputValue)}>
                <Iconify icon="eva:refresh-fill" />
              </IconButton>
            </Tooltip>
          }
        >
          {fetchError}
        </Alert>
      )}

      {/* Searchable Dropdown */}
      <Autocomplete
        fullWidth
        options={userList}
        value={selectedUser}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={(_, newValue) => onUserSelect(newValue)}
        loading={loading}
        getOptionLabel={getLabel}
        isOptionEqualToValue={(option, value) => option?.userId === value?.userId}
        filterOptions={(x) => x} // server-side filtering
        noOptionsText={inputValue ? 'No users match your search.' : 'No users available.'}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Search & Select User"
            placeholder="Type email to search..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.userId}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2">{option.email || '—'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.name ? `${option.name} · ` : ''}
                {option.userId ? `ID: ${option.userId}` : ''}
              </Typography>
            </Box>
          </li>
        )}
      />

      {/* Error message */}
      {error && (
        <FormHelperText error sx={{ mt: 0.5, ml: 1.5 }}>
          {error}
        </FormHelperText>
      )}

      {/* Selected User chip */}
      {selectedUser && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`Selected: ${selectedUser.email}${selectedUser.userId ? ` (ID: ${selectedUser.userId})` : ''}`}
            color="primary"
            variant="outlined"
            size="small"
            onDelete={() => onUserSelect(null)}
          />
        </Box>
      )}
    </Box>
  );
}

UserSelectCard.propTypes = {
  selectedUser: PropTypes.object,
  onUserSelect: PropTypes.func.isRequired,
  error: PropTypes.string,
};
